// MACHINERY V2 (Stroje) — reference lists -> FormSelect options (M2.7).
//
// Spec: data package §10 (option sources) + §13 (which reference tables are
// data-driven). Pure mapping, no fetching: the thunks stay in
// useCustomMachineForm.ts and the slices stay untouched.
//
// >>> WHAT IS TRANSLATED HERE (MACH-REF-I18N-1 P4) <<<
// The LABEL of every Machinery reference row (machine type, operation type, axis
// configuration, automation level, and the generic "Other" brand) is translated by
// the row's `code` (shared/utils/machineReferenceLabels.ts); a row without a code
// or key shows its English `name`. The VALUES are exactly what they were: the row
// id, and for the axis select the English NAME (see axisConfigurationOptions).
// Type / operation / axis / automation options are re-sorted by the localised
// label with "Other" last (the server's English order otherwise). Manufacturers
// are not reference rows: untranslated, server order.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { AxisConfiguration, MachineType, MachineTypes } from "../../models/models";
import { FormSelectOption } from "../forms/FormSelect";
import {
  getReferenceLabel,
  MachineRefKind,
  sortReferenceRows,
} from "../../../../shared/utils/machineReferenceLabels";
import { Location } from "../../components/locations/locationTypes";

type Translate = (key: string, options?: Record<string, unknown>) => string;
type RefRow = { id: string; name: string; code?: string | null };

export const isProductionLine = (types: MachineType[], typeId?: string): boolean =>
  !!typeId && types.find((t) => t.id === typeId)?.name === MachineTypes.productionLine;

export const machineTypeOptions = (
  types: MachineType[],
  translate: Translate
): FormSelectOption[] => referenceOptions(types, translate, "type");

/** Every `{id, name}` list — manufacturers (no `kind`: label = name, server order), and
 *  the reference tables (operation types, control-system brands, automation levels) with
 *  their `kind`: localised label. Value = the row id either way. */
export const referenceOptions = (
  rows: RefRow[],
  translate?: Translate,
  kind?: MachineRefKind
): FormSelectOption[] => {
  if (!kind || !translate) return rows.map((r) => ({ id: r.id, label: r.name }));
  const ordered = kind === "brand" || kind === "taper" ? rows : sortReferenceRows(rows, kind, translate);
  return ordered.map((r) => ({ id: r.id, label: getReferenceLabel(kind, r, translate) }));
};

/**
 * AXIS CONFIGURATION IS THE ODD ONE OUT — ITS VALUE IS THE NAME.
 * Data package §10: "Autocomplete onChange only clearErrors; value captured as
 * option NAME via the register spread; `handleCreateMachine` resolves
 * `axisConfigurations.find(x => x.name === value)?.id`. All other id fields
 * resolve by id." (v1: components/machineTypeForms/CncForm.tsx:152 registers
 * the Autocomplete's TextField; components/MachineDialog.tsx:351 resolves it.)
 * The quirk is preserved deliberately, so this mapper puts the NAME in `id`
 * (only the visible label is translated, MACH-REF-I18N-1). Two rows may share a
 * translated label (axis-4 / axis-4-rare -> "4 osi"); the value stays unique.
 * `resolveAxisConfigurationId` below is the other half.
 */
export const axisConfigurationOptions = (
  rows: AxisConfiguration[],
  translate: Translate
): FormSelectOption[] =>
  sortReferenceRows(rows, "axis", translate).map((r) => ({
    id: r.name,
    label: getReferenceLabel("axis", r, translate),
  }));

export const resolveAxisConfigurationId = (
  rows: AxisConfiguration[],
  value?: string
): string | undefined => rows.find((x) => x.name === value)?.id;

/**
 * Locations, flattened to the SAME shape v1's LocationPicker builds
 * (components/locations/LocationPicker.tsx:43-77): disabled rows dropped, a
 * parent WITH children contributes its children (grouped under the parent name)
 * and is not itself selectable, a childless top-level location is selectable on
 * its own. Rebuilt against FormSelect's `group` field rather than importing the
 * picker — that component is MUI (`Autocomplete`), which §3 keeps out of v2.
 *
 * The machine-count suffix is the §14 i18n gap being FIXED, not copied: v1
 * renders a hard-coded English "machine"/"machines" (LocationPicker.tsx:197).
 */
export const locationOptions = (
  locations: Location[],
  translate: Translate
): FormSelectOption[] => {
  const withCount = (name: string, count?: number): string =>
    typeof count === "number"
      ? `${name} · ${translate("myMachinery:v2.add.machineCount", { count })}`
      : name;

  const out: FormSelectOption[] = [];
  locations.forEach((loc) => {
    if (loc.isDisabled) return;
    if (loc.childLocations && loc.childLocations.length > 0) {
      loc.childLocations.forEach((child) => {
        if (child.isDisabled) return;
        out.push({
          id: child.id,
          label: withCount(child.name, child.machineCount),
          group: loc.name,
        });
      });
      return;
    }
    out.push({ id: loc.id, label: withCount(loc.name, loc.machineCount) });
  });
  return out;
};

/** Control-system MODELS feed a freeSolo text input (artboard 0803 draws an
 *  input, not a select), so they are plain suggestion strings for a datalist. */
export const controlSystemModelNames = (rows: { id: string; name: string }[]): string[] =>
  rows.map((r) => r.name);
